import { InputHTMLAttributes, Ref } from 'react';

import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { Styles, css, sva } from '@flipster/styled-system/css';
import { center, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

const recipe = sva({
  slots: ['container', 'label', 'input', 'message'],
  base: {
    label: {
      textStyle: 'Text/M/Regular',
      color: 'content.default.level-5',
    },
    container: {
      w: '100%',
      p: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      textStyle: 'Text/L/Regular',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: 4,
      transition: 'border-color 200ms',
      borderColor: 'border.default.level-3',
      _focusWithin: {
        '&:not(:disabled)': {
          borderColor: 'content.default.level-2',
          '& > button': {
            display: 'inherit',
          },
        },
      },
      _hover: {
        '&:not(:disabled)': {
          borderColor: 'content.default.level-2',
          '& > button': {
            display: 'inherit',
          },
        },
      },
      '& > button': {
        display: 'none',
      },
    },
    input: {
      width: '100%',
      color: 'content.default.level-5',
      h: 24,
    },
    message: {
      textStyle: 'Text/S/Regular',
      color: 'content.default.level-2',
      whiteSpace: 'pre-wrap',
    },
  },
  variants: {
    error: {
      true: {
        container: {
          borderColor: 'border.state.negative!',
        },
        message: {
          color: 'content.state.negative',
        },
      },
    },
  },
});

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  ref?: Ref<HTMLInputElement>;
  css?: Styles;
  error?: string | false;
  message?: string;
  label?: string;
  onXClick?: () => void;
}
export const Input = ({
  ref,
  css: cssProps,
  error,
  message,
  label,
  onChange,
  disabled,
  name,
  onXClick,
  ...inputProps
}: InputProps) => {
  const {
    container,
    input: inputStyles,
    label: labelStyles,
    message: messageStyles,
  } = recipe({ error: Boolean(error) });
  return (
    <div
      className={css(
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        },
        cssProps,
      )}
    >
      {label && (
        <label className={labelStyles} htmlFor={name}>
          {label}
        </label>
      )}
      <div className={stack({ gap: 4 })}>
        <div className={container}>
          <input
            type='text'
            onChange={onChange}
            disabled={disabled}
            size={10}
            ref={ref}
            className={inputStyles}
            name={name}
            {...inputProps}
          />
          {onXClick && (
            <button className={center()} onMouseDown={onXClick}>
              <XCircleIcon size={24} color={token('colors.content.default.level-1')} />
            </button>
          )}
        </div>
        <p className={messageStyles}>{error || message}</p>
      </div>
    </div>
  );
};
